import { useForm } from '@mantine/form';
import { useApi } from 'api/api-context';
import HrTermStateModal, { HrRelatedTermFormValues } from 'components/modals/hr-term-state/HrTermStateModal';
import panic from 'errors/panic';
import { useEffect, useState } from 'react';

interface HrPairCertificateModalProps {
  term: any | null;
  onClose: () => void;
  onPaired: (term: unknown) => void | Promise<void>;
}

/** Reusable technician-certificate pairing workflow for term tables and details. */
export default function HrPairCertificateModal({ term, onClose, onPaired }: HrPairCertificateModalProps) {
  const { getAction } = useApi();
  const [certificateOptions, setCertificateOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const form = useForm<HrRelatedTermFormValues>({
    initialValues: {
      termTypeId: '',
      nextDueDate: '',
      recurrenceQuantity: 1,
      recurrenceUnit: 'years',
      note: '',
      checkedVariantIds: [],
      certificateId: '',
      certificateNumber: '',
      renewalDate: '',
      certificateFileId: '',
      stampFileId: '',
    },
  });

  useEffect(() => {
    if (!term) {
      setCertificateOptions([]);
      setLoading(false);
      return;
    }

    let current = true;
    form.setValues({
      ...form.values,
      certificateId: '',
      certificateNumber: term.certificateNumber ?? '',
      renewalDate: term.renewalDate ?? term.nextDueDate ?? '',
      certificateFileId: term.certificateFileId ?? '',
      stampFileId: term.stampFileId ?? '',
    });
    setLoading(true);
    getAction('HrListCertificateOptions')({ parameters: { termId: String(term.termId) } })
      .then((options) => {
        if (current) setCertificateOptions(options);
      })
      .catch((caught) => {
        if (current) panic(caught as Error);
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [getAction, term]);

  const submit = form.onSubmit(async (values) => {
    if (!term) return;
    try {
      const paired = await getAction('HrPairCertificate')({
        parameters: { termId: String(term.termId) },
        payload: {
          version: term.version,
          certificateId: values.certificateId ? Number(values.certificateId) : undefined,
          certificateNumber: values.certificateNumber || undefined,
          renewalDate: values.renewalDate || undefined,
          certificateFileId: values.certificateFileId || undefined,
          stampFileId: values.stampFileId || undefined,
        },
      });
      onClose();
      await onPaired(paired);
    } catch (caught) {
      panic(caught as Error);
    }
  });

  return (
    <HrTermStateModal
      mode={term ? 'pair' : null}
      form={form}
      certificateOptions={certificateOptions}
      certificateOptionsLoading={loading}
      onClose={onClose}
      onSubmit={submit}
    />
  );
}
